import React, { useState, useEffect } from "react";
import { useSnackBar } from "../../../../../hooks/useSnackBar";
import "./settings.css";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import i18n from "../../../../../i18n";
import {
  addReadyMadeRoles,
  fetchAllPermissions,
  fetchAllRoles,
} from "../../../../../services/settings.services";
import {
  SHIPPER_ALLOWED_CHILDREN,
  SHIPPER_OPEN_ROOTS,
  SHIPPER_PERMISSION_ROOTS,
  filterPermissionTree,
} from "./Components/ManageRoles/rolePermissions";
import { isStoreOwner } from "../../../../../helpers/storeAccess";
import SideBar from "./Components/SideBar/SideBar";
import AddRole from "./Components/ManageRoles/AddRole";
import ManageRoles from "./Components/ManageRoles/ManageRoles";
import Sad from "@mui/icons-material/SentimentVeryDissatisfied";
import BoltButton from "../../../../../Components/UI/Buttons/BoltButton";
import { FcPlus } from "react-icons/fc";

const WAREHOUSE_PERMISSION_ROOTS = [
  "shipper.access.product",
  "shipper.access.order",
  "shipper.access.payment",
];

// Temporary: only expose these specific leaf permissions to warehouse users.
const WAREHOUSE_ALLOWED_CHILDREN = [
  "shipper.access.product.view",
  "shipper.access.order.view",
  "shipper.access.payment.view",
];

// What adding the ready-made roles did, for the message after it.
export const readyMadeRolesMessage = (result) => {
  const created = result?.created ?? [];
  const kept = result?.kept ?? [];
  if (created.length === 0) {
    return i18n.t("settings:roles.readyMade.allThere");
  }
  const added = i18n.t("settings:roles.readyMade.added", {
    count: created.length,
    names: created.join(", "),
  });
  return kept.length > 0
    ? i18n.t("settings:roles.readyMade.alreadyThere", {
        added,
        kept: kept.join(", "),
      })
    : added;
};

function RolesSettings(props) {
  const { t } = useTranslation("settings");
  const user = useSelector((state) => state.user);
  const [loading, setLoading] = useState(false);

  //create roles
  const [open, setOpen] = useState(false);

  const [permisions, setPermissions] = useState([]);
  const [permisionsAlter, setPermissionsAlter] = useState([]);
  const [roles, setRoles] = useState([]);
  const [addingReadyMade, setAddingReadyMade] = useState(false);
  const setSnack = useSnackBar();
  // Ready-made roles are added by the store's own login (the account owner).
  const canAddReadyMade = isStoreOwner(user);

  const openModel = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const fetchPermissionsData = async () => {
    const saved = await fetchAllPermissions();
    if (saved?.error) throw saved.error;
    const allPermissions = saved?.data?.result?.permissions?.data ?? [];
    let permissionsList;
    if (user?.role === "warehouseUser") {
      permissionsList = filterPermissionTree(
        allPermissions,
        WAREHOUSE_PERMISSION_ROOTS,
        WAREHOUSE_ALLOWED_CHILDREN
      );
    } else if (
      user?.role === "Shipper" ||
      user?.role === "ShipperUser" ||
      user?.role === "oneTimeShipper"
    ) {
      permissionsList = filterPermissionTree(
        allPermissions,
        SHIPPER_PERMISSION_ROOTS,
        SHIPPER_ALLOWED_CHILDREN,
        SHIPPER_OPEN_ROOTS
      );
    } else {
      permissionsList = allPermissions;
    }
    setPermissions(permissionsList);
    const array = permissionsList.map((obj) => ({
      key1: false,
      key2: (obj?.children ?? []).map(() => ({ subKey: false })),
    }));
    setPermissionsAlter(array);
  };
  const fetchAllRolesData = async () => {
    const saved = await fetchAllRoles();
    if (saved?.error) throw saved.error;
    const rolesList = saved?.data?.result?.data ?? [];
    setRoles(
      rolesList.map((role) => ({
        id: role.id,
        name: role.name,
        permissions: (role.permissions ?? []).map((p) => p.id),
      }))
    );
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([fetchPermissionsData(), fetchAllRolesData()]);
    } catch (error) {
      console.error("Error fetching data:", error);
      setPermissions([]);
      setPermissionsAlter([]);
      setRoles([]);
      setSnack({
        open: true,
        vertical: "top",
        horizontal: "center",
        text:
          error?.message ||
          t("roles.loadFailed"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, []);

  const handleAddReadyMadeRoles = async () => {
    setAddingReadyMade(true);
    const saved = await addReadyMadeRoles();
    if (saved.error) {
      setAddingReadyMade(false);
      setSnack({
        open: true,
        text:
          saved.error?.response?.data?.message ??
          t("roles.readyMadeFailed"),
        severity: "error",
      });
      return;
    }
    try {
      await fetchAllRolesData();
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
    setAddingReadyMade(false);
    setSnack({
      open: true,
      text: readyMadeRolesMessage(saved.data?.result),
      severity: "success",
    });
  };

  return (
    <>
      <div className="parent_wraper">
        {/* sidebar code  */}
        <SideBar active="0_0" />
        <div className="dash_table_wrapper">
          {/* right side secions  */}

          <div className="flex items-center justify-between px-1 mb-4">
            <h2 className="text-lg font-semibold text-ui-text m-0">{t("roles.title")}</h2>
            <div className="flex items-center gap-2">
              {canAddReadyMade && (
                <BoltButton
                  label={t("roles.addReadyMade")}
                  variant="outline"
                  size="sm"
                  loading={addingReadyMade}
                  disabled={addingReadyMade || loading}
                  onClick={handleAddReadyMadeRoles}
                  title={t("roles.addReadyMadeHint")}
                />
              )}
              <BoltButton icon={FcPlus} label={t("roles.create")} variant="success" size="sm" onClick={openModel} />
            </div>
          </div>
          <ManageRoles
            roles={roles}
            permisions={permisions}
            setRoles={setRoles}
            setSnack={setSnack}
            loading={loading}
          />
          <AddRole
            handleClose={handleClose}
            open={open}
            permisions={permisions}
            setRoles={setRoles}
            setSnack={setSnack}
          />
          {roles.length < 1 && permisions.length < 1 && !loading && (
            <div className="driver__screen_empty" style={{ width: "auto" }}>
              <div className="driver__scrnempt_content">
                <Sad className="driver__scrnempt_contenticon" />
                <div>{t("roles.empty")}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default RolesSettings;

